import { api } from './client';
import type { User } from '@/types';

export interface AuthResponse {
  token: string;
  user: User;
  keepSignedIn?: boolean;
}

export interface MeResponse {
  user: User;
  needsCredentials?: Array<{ id: string; email: string }>;
}

export async function signup(handle: string, password: string, displayName?: string, email?: string): Promise<AuthResponse> {
  return api.post<AuthResponse>('/auth/signup', { handle, password, displayName, email });
}

export interface SecurityQuestionPrompt {
  position: number;
  question: string;
}

export interface ChallengeResponse {
  challengeRequired: true;
  challengeId: string;
  questions: SecurityQuestionPrompt[];
}

export type LoginResult = AuthResponse | ChallengeResponse;

export async function login(handle: string, password: string, keepSignedIn = false): Promise<LoginResult> {
  return api.post<LoginResult>('/auth/login', { handle, password, keepSignedIn });
}

export async function submitChallenge(challengeId: string, answers: Record<string, string>): Promise<AuthResponse> {
  return api.post<AuthResponse>('/auth/challenge', { challengeId, answers });
}

export interface TrustedIp {
  id: string;
  ip: string;
  label: string | null;
  created_at: number;
  last_seen_at: number;
}

export interface SecurityInfo {
  enabled: boolean;
  hasQuestions: boolean;
  questions: SecurityQuestionPrompt[];
  trustedIps: TrustedIp[];
}

export async function getSecurity(): Promise<SecurityInfo> {
  return api.get<SecurityInfo>('/auth/security');
}

export async function saveSecurityQuestions(questions: { question: string; answer: string }[]): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>('/auth/security/questions', { questions });
}

export async function enableSecurity(enabled: boolean): Promise<{ ok: boolean; enabled: boolean }> {
  return api.post<{ ok: boolean; enabled: boolean }>('/auth/security/enable', { enabled });
}

export async function removeTrustedIp(id: string): Promise<{ ok: boolean }> {
  return api.delete<{ ok: boolean }>(`/auth/security/trusted/${id}`);
}

export async function logout(): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>('/auth/logout');
}

export async function refresh(): Promise<{ token: string }> {
  return api.post<{ token: string }>('/auth/refresh');
}

export async function getMe(): Promise<MeResponse> {
  return api.get<MeResponse>('/auth/me');
}

export async function updateDefaultText(font: string | null, fontSize: number | null): Promise<{ ok: boolean }> {
  return api.patch<{ ok: boolean }>('/auth/me/default-text', { font, fontSize });
}

export async function forgotPassword(email: string): Promise<{ ok: boolean; message: string }> {
  return api.post<{ ok: boolean; message: string }>('/auth/forgot', { email });
}

export async function resetPassword(token: string, password: string): Promise<{ ok: boolean; message: string }> {
  return api.post<{ ok: boolean; message: string }>('/auth/reset', { token, password });
}